import {
  ArrowCircleUp as ArrowCircleUpIcon,
  BarChart as BarChartIcon,
  AddCircle as AddCircleIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingActionsIcon,
  Cached as CachedIcon,
  PersonOff as PersonOffIcon,
  Person as PersonIcon,
  WarningOutlined,
} from '@mui/icons-material'
import {
  Container,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@mui/material'
import type { FC } from 'react'
import { useEffect } from 'react'

import DashboardStat from '@/components/DashboardStat'
import ErrorPage from '@/components/UI/ErrorPage'
import AppBarPageContainer from '@/components/UI/PageContainer'
import PageContentStickyTableHeader from '@/components/UI/PageContentStickyTableHeader'
import PaperedGridItem from '@/components/UI/PaperedGridItem'
import useUser from '@/hooks/useUser'
import { getQueueStats } from '@/lib/apollo'
import { Role } from '@/types/User'
import { hasRole } from '@/utils/role'
import { squads } from '@/utils/squad'
import UnauthorisedPage from '@/views/UnauthorisedPage'

import Row from './Row'
import TableHeader from './TableHeader'

export type Props = {
  collapsedMenu: boolean
}

const gridSpacing = 4

export const allowedRoles = [Role.admin, Role.supervisor]

const SquadsPage: FC<Props> = ({ collapsedMenu }) => {
  const { roles, customerId } = useUser()

  const hasPermissionToViewPage = hasRole(roles, allowedRoles)

  const [fetch, { data: fetchResult, loading, error }] = getQueueStats.useLazyQuery()

  useEffect(() => {
    if (!customerId || !hasPermissionToViewPage) {
      return
    }

    fetch({
      variables: {
        customerId,
      },
    })
  }, [fetch, customerId, hasPermissionToViewPage])

  const transformedData = getQueueStats.toTransformedData(fetchResult)

  return !hasPermissionToViewPage ? (
    <UnauthorisedPage />
  ) : error ? (
    <ErrorPage />
  ) : (
    <AppBarPageContainer
      loading={loading || transformedData === undefined}
      isNavigationDrawerOpen={!collapsedMenu}
      headerAppBarContent={
        <Typography component="h1" variant="h6">
          Squads
        </Typography>
      }
      pageContent={
        loading || transformedData === undefined ? (
          <></>
        ) : (
          <>
            <PageContentStickyTableHeader
              isNavigationDrawerOpen={!collapsedMenu}
              yOffset={305}
            >
              <TableHeader />
            </PageContentStickyTableHeader>
            <Container maxWidth="xl">
              <Grid
                container
                paddingTop={gridSpacing}
                paddingBottom={gridSpacing}
                spacing={gridSpacing}
              >
                <PaperedGridItem xs={12}>
                  <Stack direction="row" spacing={4}>
                    <DashboardStat
                      label="Inventory"
                      value={transformedData.totalInventoryBySquad.all}
                      icon={<BarChartIcon color="info" />}
                      isPrimaryMetric
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Added today"
                      value={transformedData.totalAddedToInventoryBySquad.all}
                      icon={<AddCircleIcon color="warning" />}
                      isPrimaryMetric
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Completed today"
                      value={transformedData.totalRemovedFromInventoryBySquad.all}
                      secondaryValue={`(${transformedData.totalAutoPassedBySquad.all} Auto | ${transformedData.totalRemovedFromInventoryBySMEsBySquad.all} SMEs)`}
                      icon={<CheckCircleIcon color="success" />}
                      isPrimaryMetric
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Missed SLA"
                      value={transformedData.totalMissedSLALastWorkingDayBySquad.all}
                      secondaryValue={`Last working day`}
                      icon={<WarningOutlined color="warning" />}
                      isPrimaryMetric
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Missed SLA"
                      value={transformedData.totalMissedSLATwoDaysInARowBySquad.all}
                      secondaryValue={`Two days in a row`}
                      icon={<WarningOutlined color="warning" />}
                      isPrimaryMetric
                    />
                  </Stack>
                </PaperedGridItem>
                <PaperedGridItem xs={12}>
                  <Stack direction="row" spacing={4}>
                    <DashboardStat
                      label="Escalations"
                      value={transformedData.totalEscalated}
                      icon={<ArrowCircleUpIcon />}
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Chased"
                      value={transformedData.totalChased}
                      icon={<PendingActionsIcon />}
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Reopened"
                      value={transformedData.totalReopened}
                      icon={<CachedIcon />}
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Unassigned"
                      value={transformedData.totalUnassigned}
                      icon={<PersonOffIcon />}
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Assigned"
                      value={transformedData.totalAssigned}
                      icon={<PersonIcon />}
                    />
                    <Divider orientation="vertical" flexItem />
                    <DashboardStat
                      label="Over 60 hours"
                      value={transformedData.totalOver60Hours}
                      icon={<PersonIcon />}
                    />
                  </Stack>
                </PaperedGridItem>
                <PaperedGridItem xs={12}>
                  <TableContainer>
                    <Table aria-label="simple table" size="small">
                      <TableHeader />
                      <TableBody>
                        {squads.map((squad, i) => (
                          <Row
                            key={i}
                            squad={squad}
                            totalEscalations={
                              transformedData.totalEscalatedBySquad[squad]
                            }
                            totalInventory={transformedData.totalInventoryBySquad[squad]}
                            totalAddedToInventory={
                              transformedData.totalAddedToInventoryBySquad[squad]
                            }
                            totalRemovedFromInventory={
                              transformedData.totalRemovedFromInventoryBySquad[squad]
                            }
                            totalMissedSLALastWorkingDay={
                              transformedData.totalMissedSLALastWorkingDayBySquad[squad]
                            }
                            totalMissedSLATwoDaysInARow={
                              transformedData.totalMissedSLATwoDaysInARowBySquad[squad]
                            }
                            totalAutoPassed={
                              transformedData.totalAutoPassedBySquad[squad]
                            }
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </PaperedGridItem>
              </Grid>
            </Container>
          </>
        )
      }
    />
  )
}

export default SquadsPage
